import { useState } from 'react';
import { verifiedImagesClient } from '../../../infrastructure/VerifiedImagesClient';
import { convertToCloudFrontUrl } from '../../../pages/sourcing/matched/[cpProductId]/image-editor/utils/imageUtils';
import type { CpGpMatchedProduct } from '../../../models/Coupang';

const dataURLtoBlob = (dataUrl: string): Blob => {
  const arr = dataUrl.split(',');
  const mime = arr[0]?.match(/:(.*?);/)?.[1] || 'image/webp';
  const bstr = atob(arr[1] || '');
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new Blob([u8arr], { type: mime });
};

export const useImageUpload = (matchedProduct?: CpGpMatchedProduct) => {
  const [uploading, setUploading] = useState(false);

  const uploadThumbnail = async (dataUrl: string, cpItemId: string): Promise<string> => {
    if (!matchedProduct) {
      throw new Error('No matched product available');
    }

    const uuid = crypto.randomUUID().replace(/-/g, '');
    const fileName = `thu_${uuid}.webp`;

    const presignedResponse = await verifiedImagesClient.getPresignedUrl({
      fileName,
      gpProductId: matchedProduct.gpProductId,
      cpProductId: matchedProduct.cpProductId,
      cpItemId: cpItemId,
    });

    if (!presignedResponse.success) {
      throw new Error(presignedResponse.message || 'Failed to get presigned URL');
    }

    const blob = dataURLtoBlob(dataUrl);
    const uploadSuccess = await verifiedImagesClient.uploadToS3(presignedResponse.uploadUrl, blob);

    if (!uploadSuccess) {
      throw new Error('Failed to upload image to S3');
    }

    const cloudFrontUrl = convertToCloudFrontUrl(presignedResponse.s3Url);

    await verifiedImagesClient.updateVerifiedImages({
      gpProductId: matchedProduct.gpProductId,
      cpProductId: matchedProduct.cpProductId,
      cpItemId: cpItemId,
      imageUrlToRemove: [],
      imageUrlToAdd: [cloudFrontUrl],
    });

    return cloudFrontUrl;
  };

  const uploadImages = async (images: Array<{ dataUrl: string; fileName: string }>): Promise<string[]> => {
    if (!matchedProduct) {
      throw new Error('No matched product available');
    }

    setUploading(true);
    try {
      const uploadedUrls: string[] = [];

      for (const { dataUrl, fileName } of images) {
        const presignedResponse = await verifiedImagesClient.getPresignedUrl({
          fileName,
          gpProductId: matchedProduct.gpProductId,
          cpProductId: matchedProduct.cpProductId,
          cpItemId: matchedProduct.cpItemId,
        });

        if (!presignedResponse.success) {
          throw new Error(presignedResponse.message || 'Failed to get presigned URL');
        }

        const blob = dataURLtoBlob(dataUrl);
        const uploadSuccess = await verifiedImagesClient.uploadToS3(presignedResponse.uploadUrl, blob);

        if (!uploadSuccess) {
          throw new Error('Failed to upload image to S3');
        }

        const cloudFrontUrl = convertToCloudFrontUrl(presignedResponse.s3Url);
        uploadedUrls.push(cloudFrontUrl);
      }

      if (uploadedUrls.length > 0) {
        await verifiedImagesClient.updateVerifiedImages({
          gpProductId: matchedProduct.gpProductId,
          cpProductId: matchedProduct.cpProductId,
          cpItemId: matchedProduct.cpItemId,
          imageUrlToRemove: [],
          imageUrlToAdd: uploadedUrls,
        });
      }

      return uploadedUrls;
    } finally {
      setUploading(false);
    }
  };

  return {
    uploadThumbnail,
    uploadImages,
    uploading,
  };
};
